import { axiosToken, simpleAxios } from "@/utils/http";
import type { AxiosResponse } from "axios";
import { defineStore, acceptHMRUpdate } from "pinia"
import {type ActionData} from "@/stores/apps/interfaces/action_interfaces"
import { useContentStore } from "./content";
import { useEventsStore } from "./events";
import dayjs from "@/utils/dayjs";
import _ from "lodash";
import { sound_alert } from  "@/utils/notifications";
import { getTranslatedObject } from "@/utils/translate";
import { updateRankingDisplayMode } from "@/composables/useRankingDisplay";
import type { RankingDisplayMode } from "@/config/rankingDisplayModes";
export const useActionStore = defineStore("action", {
	state: () => ({
        actionData: {} as ActionData, 
		forceQuiz:[],
		challenges: [], 
		current_action_team:null,
		showAnswer: false,
		answersShuffled: [],
		userAnswer: null,
		quizSelected: null,
		challengeSelected: null,
		ws : null, 
		activeChallenge: null,
		teamChallenges: [],
		playerChallenges: [],
		actionChallengeToCheck: [],
		team: [],
		challenge_to_upload:[] as any[],
		quiz_to_upload:[] as any[],
		/** Init challenge IDs submitted locally (incl. offline) until backend confirms. */
		init_challenges_pending: [] as number[],
		slides: [] as any[],
	}),
	actions: {
		clearDemoProgress() {
			this.challenge_to_upload = []
			this.quiz_to_upload = []
			this.init_challenges_pending = []
			this.actionChallengeToCheck = []
			this.playerChallenges = []
			this.teamChallenges = []
			this.forceQuiz = []
			this.current_action_team = null
			this.activeChallenge = null
			this.challengeSelected = null
			this.showAnswer = false
			this.userAnswer = null
			this.answersShuffled = []
		},
		async setRankingDisplayMode(event_api_key:string, mode: RankingDisplayMode)
		{
			await updateRankingDisplayMode(event_api_key, mode)
			this.actionData.ranking_display_mode = mode
		},
		async updateTeam(team)
		{
			if( this.team.length == 0 || !team?.id)
			{
				return
			}
			const indexUpdate = this.team.findIndex(e => e.team?.id == team.id)
			if (indexUpdate < 0) {
				return
			}
			this.team[indexUpdate].team = team;
			this.team[indexUpdate].points = team.points
		},
		async getActionData (api_key:string)
		{
			await simpleAxios("get", "events/event_full/" + api_key, {})
			.then((response: AxiosResponse) => {
                console.log(response.data);
                
                this.actionData = response.data
			})
			.catch((e: any) => {
				console.log(e);
			});
		},
		async setActionData (data:ActionData)
		{
            this.actionData = data
		},
		async setChallenges(data:any)
		{
			const foundIndex = this.playerChallenges.findIndex(x => x.id == data.id);
			if ( foundIndex >= 0 )
			{
				this.playerChallenges[foundIndex] = data;
			}
			else
			{
				this.playerChallenges.push(data)		
			}	
		},
		async fetchChallenges(api_key:string)
		{
			const contentStore = useContentStore()
			await simpleAxios("get", "ActionChallenge/get_challenge/" + api_key, {})
			.then((response: AxiosResponse) => {
				console.log(response.data);
				this.playerChallenges = response.data
			})
			.catch((e: any) => {
				console.log(e);
			});
		},
		async fetchTeamChallenges(api_key:string)
		{
			await simpleAxios("get", "ActionChallenge/get_challenge/" + api_key, {})
			.then((response: AxiosResponse) => {
				console.log(response.data);
				this.teamChallenges = response.data.filter(e => e.team == this.current_action_team.id)
				// this.setTeamChallenges(response.data)
			})
			.catch((e: any) => {
				console.log(e);
			});
		},
		async setTeamChallenges(data:any)
		{
			const mergeIncoming = (existing: any, incoming: any) => {
				if (!existing) return incoming
				// Keep local "already submitted" while upload is still pending on the server.
				if ((existing.done || existing.pending) && !incoming.done && !incoming.returned) {
					return {
						...incoming,
						done: true,
						pending: incoming.pending || existing.pending || true,
					}
				}
				return incoming
			}

			const foundIndex = this.teamChallenges.findIndex(x => x.id == data.id);
			if ( foundIndex >= 0 )
			{
				this.teamChallenges[foundIndex] = mergeIncoming(this.teamChallenges[foundIndex], data);
			}
			else
			{
				if(this.current_action_team)
				{
					if( data.team == this.current_action_team.id)
					{
						const findChallenge = this.teamChallenges.findIndex(x => x.challenge == data.challenge)
						
						if(findChallenge >= 0)
						{
							this.teamChallenges[findChallenge] = mergeIncoming(this.teamChallenges[findChallenge], data);
						}
						else
						{
							this.teamChallenges.push(data)
						}
					}
				}
			}
		},
		async fetchForceQuiz (api_key:string)
		{
			await simpleAxios("get", "ActionChallenge/get_force_quiz/" + api_key, {})
			.then((response: AxiosResponse) => {
                console.log(response.data);
                
                this.forceQuiz = response.data
			})
			.catch((e: any) => {
				console.log(e);
			});
		},
		async fetchSlides (api_key:string)
		{
			await simpleAxios("get", "ActionChallenge/get_slides/" + api_key, {})
			.then((response: AxiosResponse) => {
                console.log(response.data);
                
                this.slides = response.data
			})
			.catch((e: any) => {
				console.log(e);
				this.slides = []
			});
		},
		setChallengeDone( active_challenge_id)
		{
			const challenge = this.teamChallenges.find(e => e.id == active_challenge_id)
			if (!challenge) return
			// done + pending: player UI moves to "return material" while media still uploads;
			// WS pending updates must not reopen the camera (see setTeamChallenges).
			challenge.done = true
			challenge.pending = true
		},
		setChallengeInitDone( active_challenge_id)
		{
			if (!this.init_challenges_pending.includes(active_challenge_id)) {
				this.init_challenges_pending.push(active_challenge_id)
			}
			const existing = this.teamChallenges.find(e => e.challenge == active_challenge_id)
			if (existing) {
				existing.done = true
				existing.returned = true
				if (existing.game_master_check == null) {
					existing.game_master_check = false
				}
			} else {
				this.teamChallenges.push({
					challenge: active_challenge_id,
					done: true,
					returned: true,
					game_master_check: false,
				})
			}
		},
		updateActionChallengeToCheck(data:any)
		{
			
			this.actionChallengeToCheck = data;
		},
	},
	persist: {
		// Never persist upload queues: File/Blob JSON-serializes to {} and poisons FormData.
		afterRestore: (ctx) => {
			ctx.store.challenge_to_upload = []
			ctx.store.quiz_to_upload = []
		},
	},
	getters:{
		getSecondsTime: (state) =>{
			if(state.actionData.timer_finish == undefined)
			{
				return;
			}
			const now:Date = new Date();			
			return new Date(state.actionData.timer_finish);
		},
		getNumberQuiz: state=> (id:number) =>{
			const contentStore = useContentStore();
			const categoryInfo = {
				total:0,
				done:0 
			}
			
			if( !('action_challenge_quiz' in contentStore.content))
			{
				return categoryInfo;
			}
			if( state.current_action_team == null )
			{
				return categoryInfo;
			}
			contentStore.content.action_challenge_quiz.forEach(e => {
				if( e.category == id )
				{
					categoryInfo.total ++;
					if(state.current_action_team.challenge_quiz.includes(e.id))
					{
						categoryInfo.done ++;
					}
				}
			});
			return categoryInfo;
		},
		getQuizFromCategory: state=> (id:number) =>{
			const contentStore = useContentStore();
			if( !('action_challenge_quiz' in contentStore.content))
			{
				return [];
			}
			
			return contentStore.content.action_challenge_quiz.filter(e=>  e.category == id).sort((a,b) => a.name.localeCompare(b.name))
		
		},
		getQuizDone: state=> (id:number) =>{
			if( state.current_action_team == null )
			{
				return false;
			}
			if( state.current_action_team.challenge_quiz.includes(id))
			{
				return true;
			}
			return false;
		},
		getActiveChallenge: (state) =>{
			const contentStore = useContentStore();
			if(state.teamChallenges.length == 0)
			{
				return null
			}

			const challenge = state.teamChallenges.find(e => e.returned == false)
			if( challenge)
			{				
				challenge.data = contentStore.getContentByTypeById("action_challenge_challenge", challenge.challenge)
			}
			return challenge
		},
		getChallengeStatus: state=> (id:number) =>{
			const contentStore = useContentStore();
			let status = 'ready';
			
			const challenge_data = contentStore.getContentByTypeById("action_challenge_challenge", id)
			if(state.current_action_team.money < challenge_data.cost)
			{
				status = "no_money"
			}
			else if(id.toString() in state.actionData.challenge_store && state.actionData.challenge_store[id] <= 0)
			{
				status = "no_material"
			}

			const challenge = state.teamChallenges.find(e => e.challenge == id)
			if(challenge != undefined)
			{
				if(!challenge.returned)
				{
					status = "active"
				}
				else if(challenge.game_master_check)
				{
					status = "done"	
				}
				else
				{
					status = "no_review"	
				}
			}
			
			return status
		},
		getChallengeInitStatus: state=> (id:number) =>{
			let status = 'ready';

			const challenge = state.teamChallenges.find(e => e.challenge == id)
			if(challenge != undefined)
			{
				if(!challenge.returned && !challenge.done)
				{
					status = "active"
				}
				else if(challenge.returned && challenge.game_master_check)
				{
					status = "done"	
				}
				else if(challenge.returned && !challenge.game_master_check)
				{
					status = "no_review"	
				}
				else if(challenge.done)
				{
					// Submitted locally / pending upload or review
					status = "no_review"
				}
			}

			if (status === "ready") {
				if (state.init_challenges_pending.includes(id)) {
					status = "no_review"
				} else if (state.challenge_to_upload.some(e => e.challenge == id && e.init)) {
					status = "no_review"
				}
			}
			
			return status
		},
		getActionChallengeToCheck: (state) =>{
			const challengeToCheck = []
			state.playerChallenges.forEach((ch) =>{
				if( ch.done == true && ch.game_master_check == false)
				{
					const team = state.team.find(e => e.id == ch.team)	
					if( team != undefined)
					{
						challengeToCheck.push(
							{
								challenge:ch,
								team_name:team.team.name_id + " - " + team.team.name,
								team_id: team.id
							}
						);

					}
										
				}
			})			
			return challengeToCheck;
		},
		getTotalPoints: state =>{
			let points = 0
			state.team.forEach(e=> {
				if(e.team)
				{
					points += e.team.points
				}
			})
			return points
		},
		getChallengesNSelect: state =>{
			let options = []
			const contentStore = useContentStore()
			state.playerChallenges.forEach((e)=>{
				if(e.game_master_check == true)
				{
					return
				}
				options.push({
					label:getTranslatedObject(contentStore.getContentByTypeById('action_challenge_challenge', e.challenge),'name') ,
					value:e.challenge,
				}
				)
			})
			return options
		},
		getItemsToPick : state => () =>{
			const storeContent = useContentStore()

			console.log(state.actionData.store_queue);

			const rawStoreQueue = state.actionData?.store_queue
			const storeQueue = Array.isArray(rawStoreQueue)
				? rawStoreQueue
				: rawStoreQueue && typeof rawStoreQueue === "object"
					? Object.values(rawStoreQueue)
					: []
			
			if(storeQueue.length == 0 || state.team.length == 0)
			{
				return []
			}
			const itemsToPick = [] as Array<Object>
			let custom_id = 0
			storeQueue.forEach((e:any) =>{
				const team = state.team.find(team => team.id == e.team_id)
				const item = storeContent.getContentByTypeById('action_item', e.item_id)
				if(!team || !item)
				{
					return
				}
				itemsToPick.push({
					team: team.team,
					item: item,
					item_name: getTranslatedObject(item,'name' ),
					custom_id,
					done: e.delivered,

				})
				custom_id ++;

			})
			return itemsToPick
		},
		getDataPick: state => () => {			
			const storeEvents = useEventsStore()
			const storeContent = useContentStore()

			if(state.playerChallenges.filter(e => e.picked == false).length == 0 || Object.keys(state.playerChallenges.filter(e => e.picked == false)).length == 0)
			{				
				return []
			}
			
			const challengesToPick = [] as Array<Object>
			console.log(state.playerChallenges);
			
			state.playerChallenges.filter(e => e.picked == false).forEach(oq => {
				const actionTeam = state.team.find(t => t.id == oq.team)
				if (!actionTeam?.team?.id) {
					return
				}
				const team = storeEvents.getTeambyId(actionTeam.team.id)
				const challenge = storeContent.getContentActionChallenge(parseInt(oq.challenge))
				if (!team || !challenge) {
					return
				}
				challengesToPick.push({team: team, challenge: challenge, id: oq.id, team_name: team.name, item_name: challenge.name, picked: challenge.picked, team_name_with_id: team.name_id + ". " + team.name})
			});

			return challengesToPick
		},
		getDataReturn: state => () => {
			const storeEvents = useEventsStore()
			const storeContent = useContentStore()

			// Include pending (media still uploading) so monitor can accept material return immediately.
			const toReturn = state.playerChallenges.filter(
				e => e.returned == false && (e.done == true || e.pending == true)
			)
			if(toReturn.length == 0)
			{
				return []
			}

			const challengesToPick = [] as Array<Object>

			toReturn.forEach(oq => {
				const actionTeam = state.team.find(t => t.id == oq.team)
				if (!actionTeam?.team?.id) {
					return
				}
				const team = storeEvents.getTeambyId(actionTeam.team.id)
				const challenge = storeContent.getContentActionChallenge(parseInt(oq.challenge))
				if (!team || !challenge) {
					return
				}
				challengesToPick.push({team: team, challenge: challenge, id: oq.id, team_name: team.name, item_name: challenge.name, returned: challenge.returned, team_name_with_id: team.name_id + ". " + team.name})
			});

			return challengesToPick
		},
		getDataPublicRanking: state => () =>
		{
			if(state.team.length == 0)
			{
				return []
			}
			console.log("calling");
			const teamData = [...state.team]
			// return teamData

			return teamData.sort((a, b) => b.team.points - a.team.points || b.money - a.money )
		},
		getDataGameMasterRanking: state => () => {
			const eventStore = useEventsStore()
			const contentStore = useContentStore() 
			const dataTeam = JSON.parse(JSON.stringify(state.team));
			const ranking = dataTeam.sort((a, b) => b.team.points - a.team.points || b.money - a.money)
			console.log(ranking);
			
			const categories = []
			contentStore.content.category_quiz.forEach(e => {
				e.total_quizzes = contentStore.content.action_challenge_quiz.filter(ac => ac.category == e.id).length
				e.total_player = 0
				categories.push(e)
			});

			ranking.forEach(r => {
				let challenges_done = 0
				let quizzes_done = 0
				let last_challenge = ""
				let last_challenge_start = null
				const challenges_status = []
				console.log(r)
				state.playerChallenges.sort((a, b) => a.team_id - b.team_id).forEach(pc => {
					if(pc.team == r.id)
					{
						let challenge_data = undefined
						challenge_data = contentStore.content.action_challenge_challenge.find(ac => ac.id == pc.challenge)
							
						if(pc.done)
						{
							challenges_done++
							last_challenge = challenge_data.name
						}
						
						pc.data = challenge_data
						challenges_status.push(pc)
					}
				})
				const player_categories = JSON.parse(JSON.stringify(categories));
				const dataChallengeQuiz = JSON.parse(JSON.stringify(contentStore.content.action_challenge_quiz))
				r.challenge_quiz.forEach(e => {
					const quiz = dataChallengeQuiz.find(ac => ac.id == e)
					const category = player_categories.find(c => c.id == quiz.category )
					category.total_player++
					quizzes_done++
				});
				r.player_categories = player_categories
				r.challenges_status = challenges_status
				r.full_name = r.team.name_id + ". " + r.team.name
				r.challenges_done = challenges_done
				r.quizzes_done = quizzes_done
				r.last_challenge = last_challenge
				r.points = r.team.points
				r.key = r.id
			});

			return ranking
		},
		getChallengesWithStatus: state => () => {
			const contentStore = useContentStore()
			const actionStore = useActionStore()
			if(!('action_challenge_challenge' in contentStore.content))
			{
				return []
			}

			const challenges = contentStore.content.action_challenge_challenge.filter(e => e.init_challenge == false)
			
			challenges.forEach(element => {
				element.status = actionStore.getChallengeStatus(element.id)
			});
			return challenges
		},
		getChallengesInitWithStatus: state => () => {
			const contentStore = useContentStore()
			const actionStore = useActionStore()
			if(!('action_challenge_challenge' in contentStore.content))
			{
				return []
			}

			const challenges = contentStore.content.action_challenge_challenge.filter(e => e.init_challenge == true)
			
			challenges.forEach(element => {
				element.status = actionStore.getChallengeInitStatus(element.id)
			});
			return challenges
		},
		getTeamRanking: state => (sortByPoints = true): any | undefined => {
			let ranking = []
			if(sortByPoints)
			{
				ranking = state.team.sort((a, b) => b.team.points - a.team.points || b.money - a.money)
			}
			else
			{
				ranking = state.team.sort((a, b) => b.money - a.money || b.team.points - a.team.points)
			}
			console.log(ranking);
			
			let pos = 1
			let beforePoints = -1
			ranking.forEach(r => {
				if(beforePoints != -1 && beforePoints != r.team.points)
				{
					pos++
				}
				beforePoints = r.team.points
				r.position = pos
				r.points = r.team.points
				
			});
			console.log(ranking);
			
			return ranking
		},
		getChallenges: state => () => {
			const contentStore = useContentStore()
			const challengeSelect = []
			// check if challenge has been picked
			contentStore.content.action_challenge_challenge_store.forEach(e => {
				let disable = false;
				if( state.actionData.current_challenge_done.includes(e.id) )
				{
					disable = true;
				}
				challengeSelect.push({
					label: getTranslatedObject(e, "name") || e.name,
					value: e.id,
					disabled: disable
				})
			})
			return challengeSelect
		},
		geActionChallengeSelected: state => () => {
			const storeEvents = useEventsStore()
			
			if(state.actionData.challenge_done_by_team && Array.isArray(state.actionData.challenge_done_by_team)) 
			{
				const teams = [] as Array<Object>				
				storeEvents.teams.forEach(t => {
					const t_copy = JSON.parse(JSON.stringify(t))
					// if (Object.keys(state.actionData.challenge_done_by_team).length === 0)
					// {
					// 	state.actionData.challenge_done_by_team = []
					// }
					if( !state.actionData.challenge_done_by_team.includes(t.id))
					{
						t_copy.done = false
						t_copy.name_with_id = t_copy.name_id + ". " + t_copy.name
						teams.push(t_copy)
					}
				});
				
				return teams
			}
			else
			{
				return []
			}
		},
		getActiveChallengePlayer: state => () => {
			
			if(state.actionData.current_challenge != null && state.current_action_team != null)
			{	
							
				if( state.actionData.challenge_done_by_team.includes(state.current_action_team.team.id) )
				{
					return false
				}
				return true
			}
			return false
		},
		getFavorites: state => () => 
			{
				const eventStore = useEventsStore()
				const contentStore = useContentStore() 
				const dataTeam = JSON.parse(JSON.stringify(state.team));
				const teams = dataTeam.sort((a, b) => a.team.name.localeCompare(b.team.name))
				console.log(teams);
	
				teams.forEach(r => {
					const challenges_status = []
					console.log(r)
					state.playerChallenges.sort((a, b) => a.team_id - b.team_id).forEach(pc => {
						if(pc.team == r.id)
						{
							console.log(pc);
							
							// let challenge_data = undefined
							// challenge_data = contentStore.content.action_challenge_challenge.find(ac => ac.id == pc.challenge)
							
							// pc.data = challenge_data
							challenges_status.push(pc)
						}
					})
					r.challenges_status = challenges_status
					r.full_name = r.team.name_id + ". " + r.team.name
				});
				teams.sort((a, b) => a.team.fav - b.team.fav)
				return teams
			},
	}
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useActionStore, import.meta.hot))
}
